import React, { useState, useEffect } from "react"
import { useTranslation } from "@zestia/gatsby-plugin-react-i18next"
import FeatureTick from "@/components/FeatureTick"
import IconChevronDown from "@/components/svg/icons/IconChevronDown"
import HelpTooltip from "@/components/HelpTooltip"
import { formatNumber } from "@/utils/format-number"
import { gtmSignupStart } from "@/utils/gtm-tracking"

const FeatureTable = ({
  showSignupButtons = true,
  onSelectPlan,
  inModal = false,
  hideUltimateColumn = false,
}) => {
  const { t } = useTranslation()
  const [mobileViewIndex, setMobileViewIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  /**
   * @type {import ("./feature-table-data.d.ts").default}
   */
  const data = {
    plans: hideUltimateColumn
      ? ["free", "starter", "growth", "advanced"]
      : ["free", "starter", "growth", "advanced", "ultimate"],
    sections: [
      {
        name: t("feature-table.section.keep-everything-in-one-place"),
        features: [
          {
            name: t("feature-table.feature.contacts.name"),
            tooltipText: t("feature-table.feature.contacts.tooltip-text"),
            values: [
              { plan: "ultimate", value: formatNumber("240000") },
              { plan: "advanced", value: formatNumber("120000") },
              { plan: "growth", value: formatNumber("60000") },
              { plan: "starter", value: formatNumber("30000") },
              { plan: "free", value: "250" },
            ],
          },
          {
            name: t("feature-table.feature.file-storage.name"),
            tooltipText: t("feature-table.feature.file-storage.tooltip-text"),
            values: [
              {
                plan: "ultimate",
                value: t("feature-table.feature.file-storage.data.ultimate"),
              },
              {
                plan: "advanced",
                value: t("feature-table.feature.file-storage.data.advanced"),
              },
              {
                plan: "growth",
                value: t("feature-table.feature.file-storage.data.growth"),
              },
              {
                plan: "starter",
                value: t("feature-table.feature.file-storage.data.starter"),
              },
              {
                plan: "free",
                value: t("feature-table.feature.file-storage.data.free"),
              },
            ],
          },
          {
            name: t("feature-table.feature.log-activity.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.import-customers.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.contact-history.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.email-storage.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.tag-management.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t(
              "feature-table.feature.integrate-with-accounting-platforms.name",
            ),
            tooltipText: t(
              "feature-table.feature.integrate-with-accounting-platforms.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: false },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.build-stronger-relationships"),
        features: [
          {
            name: t("feature-table.feature.email-templates.name"),
            tooltipText: t(
              "feature-table.feature.email-templates.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: formatNumber("1000") },
              { plan: "advanced", value: "500" },
              { plan: "growth", value: "50" },
              { plan: "starter", value: "5" },
              { plan: "free", value: "0" },
            ],
          },
          {
            name: t("feature-table.feature.outlook-add-in.name"),
            tooltipText: t("feature-table.feature.outlook-add-in.tooltip-text"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.gmail-add-on.name"),
            tooltipText: t("feature-table.feature.gmail-add-on.tooltip-text"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.send-emails-within-capsule.name"),
            tooltipText: t(
              "feature-table.feature.send-emails-within-capsule.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: false },
            ],
          },
          {
            name: t(
              "feature-table.feature.send-emails-with-outlook-or-gmail.name",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.email-sync.name"),
            tooltipText: t("feature-table.feature.email-sync.tooltip-text"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.custom-titles.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.reminder-follow-up-tasks.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.track-customer-milestones.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.tailor-to-fit-your-business"),
        features: [
          {
            name: t("feature-table.feature.custom-fields.name"),
            values: [
              {
                plan: "ultimate",
                value: t("feature-table.data.common.unlimited"),
              },
              { plan: "advanced", value: "300" },
              { plan: "growth", value: "150" },
              { plan: "starter", value: "50" },
              { plan: "free", value: "5" },
            ],
          },
          {
            name: t(
              "feature-table.feature.zapier-integrations-automations.name",
            ),
            tooltipText: t(
              "feature-table.feature.zapier-integrations-automations.tooltip-text",
            ),
            values: [
              {
                plan: "ultimate",
                value: t(
                  "feature-table.feature.zapier-integrations-automations.data.value",
                ),
              },
              {
                plan: "advanced",
                value: t(
                  "feature-table.feature.zapier-integrations-automations.data.value",
                ),
              },
              {
                plan: "growth",
                value: t(
                  "feature-table.feature.zapier-integrations-automations.data.value",
                ),
              },
              {
                plan: "starter",
                value: t(
                  "feature-table.feature.zapier-integrations-automations.data.value",
                ),
              },
              {
                plan: "free",
                value: t(
                  "feature-table.feature.zapier-integrations-automations.data.value",
                ),
              },
            ],
          },
          {
            name: t("feature-table.feature.customized-filtered-lists.name"),
            tooltipText: t(
              "feature-table.feature.customized-filtered-lists.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.api-access.name"),
            tooltipText: t("feature-table.feature.api-access.tooltip-text"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.premium-add-ons.name"),
            tooltipText: t(
              "feature-table.feature.premium-add-ons.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.important-fields.name"),
            tooltipText: t(
              "feature-table.feature.important-fields.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.custom-activity-types.name"),
            tooltipText: t(
              "feature-table.feature.custom-activity-types.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.win-more-business"),
        features: [
          {
            name: t("feature-table.feature.multiple-sales-pipelines.name"),
            tooltipText: t(
              "feature-table.feature.multiple-sales-pipelines.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: "100" },
              { plan: "advanced", value: "50" },
              { plan: "growth", value: "5" },
              { plan: "starter", value: "1" },
              { plan: "free", value: "1" },
            ],
          },
          {
            name: t("feature-table.feature.kanban-visual-pipeline.name"),
            tooltipText: t(
              "feature-table.feature.kanban-visual-pipeline.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t(
              "feature-table.feature.manage-your-sales-opportunities.name",
            ),
            tooltipText: t(
              "feature-table.feature.manage-your-sales-opportunities.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.customizable-sales-milestones.name"),
            tooltipText: t(
              "feature-table.feature.customizable-sales-milestones.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.highlight-stale-opportunities.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.record-lost-reasons.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.workflow-automation.name"),
            tooltipText: t(
              "feature-table.feature.workflow-automation.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.assign-sales-teams.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.get-more-done"),
        features: [
          {
            name: t("feature-table.feature.tracks.name"),
            tooltipText: t("feature-table.feature.tracks.tooltip-text"),
            values: [
              {
                plan: "ultimate",
                value: t("feature-table.data.common.unlimited"),
              },
              {
                plan: "advanced",
                value: t("feature-table.data.common.unlimited"),
              },
              {
                plan: "growth",
                value: t("feature-table.data.common.unlimited"),
              },
              {
                plan: "starter",
                value: t("feature-table.data.common.unlimited"),
              },
              { plan: "free", value: t("feature-table.data.common.unlimited") },
            ],
          },
          {
            name: t("feature-table.feature.multiple-project-boards.name"),
            tooltipText: t(
              "feature-table.feature.multiple-project-boards.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: "100" },
              { plan: "advanced", value: "50" },
              { plan: "growth", value: "5" },
              { plan: "starter", value: "1" },
              { plan: "free", value: "0" },
            ],
          },
          {
            name: t("feature-table.feature.task-management.name"),
            tooltipText: t(
              "feature-table.feature.task-management.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.ical-subscription.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.calendar-week-view.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.project-management.name"),
            tooltipText: t(
              "feature-table.feature.project-management.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.daily-task-reminder-emails.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.work-better-with-ai"),
        features: [
          {
            name: t("feature-table.feature.24-7-ai-support.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.ai-content-assists.name"),
            tooltipText: t(
              "feature-table.feature.ai-content-assists.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: formatNumber("1000") },
              { plan: "advanced", value: formatNumber("1000") },
              { plan: "growth", value: formatNumber("1000") },
              { plan: "starter", value: "10" },
              { plan: "free", value: "10" },
            ],
          },

          {
            name: t("feature-table.feature.business-enrichment.name"),
            tooltipText: t(
              "feature-table.feature.business-enrichment.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.contact-enrichment.name"),
            tooltipText: t(
              "feature-table.feature.contact-enrichment.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.ai-sales-pipeline-generator.name"),
            tooltipText: t(
              "feature-table.feature.ai-sales-pipeline-generator.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.ai-summaries.name"),
            label: t("feature-table.labels.beta"),
            tooltipText: t("feature-table.feature.ai-summaries.tooltip-text"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.ai-meeting-prep.name"),
            tooltipText: t(
              "feature-table.feature.ai-meeting-prep.tooltip-text",
            ),
            label: t("feature-table.labels.beta"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.collaborate"),
        features: [
          {
            name: t("feature-table.feature.send-emails-shared-mailboxes.name"),
            tooltipText: t(
              "feature-table.feature.send-emails-shared-mailboxes.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: "10" },
              { plan: "advanced", value: "5" },
              { plan: "growth", value: "3" },
              { plan: "starter", value: "1" },
              { plan: "free", value: "0" },
            ],
          },
          {
            name: t("feature-table.feature.organize-users-into-teams.name"),
            values: [
              { plan: "ultimate", value: "100" },
              { plan: "advanced", value: "50" },
              { plan: "growth", value: "5" },
              { plan: "starter", value: "0" },
              { plan: "free", value: "0" },
            ],
          },
          {
            name: t("feature-table.feature.assignment-emails.name"),
            tooltipText: t(
              "feature-table.feature.assignment-emails.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.see-everyones-calendar.name"),
            tooltipText: t(
              "feature-table.feature.see-everyones-calendar.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.connect-to-outlook-calendar.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.comments.name"),
            tooltipText: t("feature-table.feature.comments.tooltip-text"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.gain-more-insight"),
        features: [
          {
            name: t("feature-table.feature.sales-pipeline-dashboard.name"),
            tooltipText: t(
              "feature-table.feature.sales-pipeline-dashboard.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.goals.name"),
            tooltipText: t("feature-table.feature.goals.tooltip-text"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.reporting-dashboards.name"),
            tooltipText: t(
              "feature-table.feature.reporting-dashboards.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.activity-reporting.name"),
            tooltipText: t(
              "feature-table.feature.activity-reporting.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.reporting-by-team.name"),
            tooltipText: t(
              "feature-table.feature.reporting-by-team.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.advanced-sales-reporting.name"),
            tooltipText: t(
              "feature-table.feature.advanced-sales-reporting.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t(
              "feature-table.feature.custom-reports-via-google-data-studio.name",
            ),
            tooltipText: t(
              "feature-table.feature.custom-reports-via-google-data-studio.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.stay-secure"),
        features: [
          {
            name: t("feature-table.feature.two-factor-authentication.name"),
            tooltipText: t(
              "feature-table.feature.two-factor-authentication.tooltip-text",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.export-for-backup.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.restrict-exports.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.microsoft-365-sso.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.google-workspace-sso.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.restrict-users-to-own-records.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t(
              "feature-table.feature.restrict-team-access-to-records.name",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.work-from-anywhere"),
        features: [
          {
            name: t("feature-table.feature.mobile-app.name"),
            tooltipText: t("feature-table.feature.mobile-app.tooltip-text"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.mobile-app-offline-support.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.online-access-uptime.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.caller-id-support.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.tablet-support.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
        ],
      },
      {
        name: t("feature-table.section.make-the-most-of-capsule"),
        features: [
          {
            name: t("feature-table.feature.human-support.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.resources.name"),
            tooltipText: t("feature-table.feature.resources.tooltip-text"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: true },
              { plan: "growth", value: true },
              { plan: "starter", value: true },
              { plan: "free", value: true },
            ],
          },
          {
            name: t("feature-table.feature.import-assistance.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: false },
              { plan: "growth", value: false },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.custom-training.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: false },
              { plan: "growth", value: false },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t(
              "feature-table.feature.dedicated-customer-success-manager.name",
            ),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: false },
              { plan: "growth", value: false },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
          {
            name: t("feature-table.feature.implementation-support.name"),
            values: [
              { plan: "ultimate", value: true },
              { plan: "advanced", value: false },
              { plan: "growth", value: false },
              { plan: "starter", value: false },
              { plan: "free", value: false },
            ],
          },
        ],
      },
    ],
  }

  // Generate sliding pairs of two plans and move one at a time.
  const getVisiblePlans = () => {
    if (!isMobile) {
      return data.plans
    }

    // Create sliding pairs - each view shows 2 plans, moving 1 at a time
    const maxIndex = data.plans.length - 2 // Maximum index for showing 2 plans
    const currentIndex = Math.min(mobileViewIndex, maxIndex)

    return [data.plans[currentIndex], data.plans[currentIndex + 1]]
  }

  const visiblePlans = getVisiblePlans()

  const maxMobileIndex = data.plans.length - 2 // Can show up to index 3 (advanced + ultimate)
  const canGoPrevious = mobileViewIndex > 0
  const canGoNext = mobileViewIndex < maxMobileIndex

  const handlePrevious = () => {
    if (canGoPrevious) {
      setMobileViewIndex(mobileViewIndex - 1)
    }
  }

  const handleNext = () => {
    if (canGoNext) {
      setMobileViewIndex(mobileViewIndex + 1)
    }
  }

  return (
    <div
      className={`feature-table ${inModal ? "feature-table--in-modal" : ""}`}
    >
      <div className="feature-table__section feature-table__head feature-table__section-plans">
        <div className="feature-table__row feature-table__row--buttons">
          <button
            type="button"
            className="feature-table__nav-arrow feature-table__nav-arrow--prev"
            onClick={handlePrevious}
            disabled={!canGoPrevious || !isMobile}
            aria-label="Previous plans"
          >
            <img
              src="/icons/arrow-right.svg"
              className="feature-table__nav-arrow-icon"
              alt=""
              width="32"
              height="32"
              style={{ transform: "rotate(180deg)" }}
            />
          </button>

          {data.plans.map(plan => {
            const isVisible = visiblePlans.includes(plan)
            return (
              <span
                key={plan}
                className={`mb-0 feature-table__cell feature-table__head-cell ${
                  isMobile && !isVisible ? "feature-table__cell--hidden" : ""
                }`}
              >
                <b>{t(`plans.name.${plan}`)}</b>

                {showSignupButtons && (
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={() => {
                      gtmSignupStart(plan)()
                      onSelectPlan(plan)
                    }}
                  >
                    {plan === "free"
                      ? t("common.get-started")
                      : t("common.try-for-free")}
                  </button>
                )}
              </span>
            )
          })}

          <button
            type="button"
            className="feature-table__nav-arrow feature-table__nav-arrow--next"
            onClick={handleNext}
            disabled={!canGoNext || !isMobile}
            aria-label="Next plans"
          >
            <img
              src="/icons/arrow-right.svg"
              className="feature-table__nav-arrow-icon"
              alt=""
              width="32"
              height="32"
            />
          </button>
        </div>
      </div>

      {data.sections.map((section, index) => (
        <FeatureTableSection
          key={section.name}
          className={index === 0 ? "feature-table__section--first" : ""}
          plans={visiblePlans}
          allPlans={data.plans}
          section={section}
          isMobile={isMobile}
        />
      ))}
    </div>
  )
}

const FeatureTableSection = ({
  plans,
  allPlans,
  section,
  className = "",
  isMobile = false,
}) => {
  const [isClosed, setIsClosed] = useState(false)

  return (
    <div
      className={`feature-table__section ${
        isClosed ? "" : "feature-table__section--expanded"
      } ${className}`}
    >
      <button
        type="button"
        className="feature-table__section-head"
        onClick={() => {
          setIsClosed(isClosed => {
            return !isClosed
          })
        }}
      >
        <IconChevronDown className="feature-table__section-head-arrow" />
        <span className="feature-table__section-head-text">{section.name}</span>
      </button>

      <div className="feature-table__section-content">
        {section.features.map(feature => {
          return (
            <div className="feature-table__row" key={feature.name}>
              <span className="feature-table__row-title">
                {feature.tooltipText ? (
                  <HelpTooltip content={feature.tooltipText}>
                    {feature.name}
                  </HelpTooltip>
                ) : (
                  feature.name
                )}

                {feature.label && (
                  <span className="ml-2 tag">{feature.label}</span>
                )}
              </span>

              {allPlans.map(plan => {
                const featureValue = feature.values.find(
                  value => value.plan === plan,
                )
                const isVisible = plans.includes(plan)

                return (
                  <FeatureTableValue
                    key={featureValue.plan}
                    className={`feature-table__cell ${
                      isMobile && !isVisible
                        ? "feature-table__cell--hidden"
                        : ""
                    }`}
                    value={featureValue.value}
                  />
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const FeatureTableValue = ({ className, value }) => {
  if (typeof value === "boolean") {
    return (
      <span className={className}>
        {value ? (
          <FeatureTick />
        ) : (
          <svg
            width="17"
            height="1"
            viewBox="0 0 17 1"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="0.347656" width="16" height="1" fill="#1E2021" />
          </svg>
        )}
      </span>
    )
  }

  if (typeof value === "string") {
    return <span className={className}>{value}</span>
  }
}

export default FeatureTable
